import { useEffect, type ReactElement } from 'react'
import { useSyncExternalStore } from 'react'
import { useMetroMapStore } from './useMetroMapStore'

/**
 * Drives the app-wide dark theme from the Night Tube mode (TASK-412 M,
 * option (i) — mirror iOS). While `currentMode === 'night'` the whole app
 * forces the dark `--mm-color-*` palette by setting
 * `documentElement.dataset.theme = 'dark'` (the `:root[data-theme='dark']`
 * block in index.css). On `normal` the attribute is REMOVED, falling back
 * to the OS `@media (prefers-color-scheme: dark)` preference.
 *
 * Setting the attribute on `<html>` (documentElement) means it cascades to
 * EVERYTHING — including React portals / modals that mount under
 * `document.body` outside the app root (TASK-412 O: the Suggester panel
 * + modal portals theme via the same `--mm-color-*` tokens, so the dark
 * palette reaches them automatically; no per-portal class needed because
 * the tokens resolve against the nearest `:root`, which is `<html>`).
 *
 * Mounted once at AppShell. Renders nothing.
 */
export function useNightThemeEffect(): void {
  const store = useMetroMapStore()
  const currentMode = useSyncExternalStore(store.subscribe, () => store.getState().currentMode)

  useEffect(() => {
    const html = document.documentElement
    if (currentMode === 'night') {
      html.dataset.theme = 'dark'
    } else {
      // Remove (not set 'light') so the OS prefers-color-scheme media query
      // resumes control for non-night users on a dark-mode OS.
      delete html.dataset.theme
    }
    return () => {
      // On unmount, restore OS control (defensive — AppShell lives for the
      // session, so this only runs in tests / fast-refresh).
      delete html.dataset.theme
    }
  }, [currentMode])
}

/** Mounts {@link useNightThemeEffect}. Renders nothing. */
export function NightThemeController(): ReactElement | null {
  useNightThemeEffect()
  return null
}
